import * as maplibregl from "maplibre-gl";
import type { Marker } from "maplibre-gl";
import { h, render, watch } from "vue";
import type { MapCtx } from "../map/mapContext";
import { markerElement, PIN_SIZE } from "../map/mapUiHelpers";
import { mapSheetCover, mapSheetInset } from "./useMapSheetInset";
import { buildPoiIconSvg } from "../map/poiIcons";
import { useSearchFocusStore, type SearchFocus } from "../store/searchFocus";
import { pickLocalizedName } from "../search/names";
import { hasKindGlyph, kindIcon } from "../search/kinds";

const ROUTE_SOURCE = "search-route";
const ROUTE_LAYER = "search-route-line";
// Зум, к которому летим к выбранной точке, и с какого точку на экране уже
// «видно» без перелёта.
const FOCUS_ZOOM = 14;
// Слои с подписями POI: у выбранной точки подпись слоя прячется, её место
// занимает плашка выбранной метки (она выше кольца и на языке интерфейса).
const POI_LABEL_LAYERS = ["pois-label", "pois-peak", "pois-pass", "pois-peak-admin", "pois-pass-admin"];
const CLOSE_ZOOM = 13;

// Рисует на карте выбранный результат поиска: пин места/точки, мелкие пины
// ближайших точек, подсвеченную линию маршрута. Вся логика состояния - в
// сторе searchFocus, здесь только отображение.
export function useSearchLayers(ctx: MapCtx) {
  const store = useSearchFocusStore();
  let pins: Marker[] = [];

  const clearPins = () => {
    pins.forEach((m) => m.remove());
    pins = [];
  };
  // Исходный text-opacity слоёв подписей, пока одна из подписей спрятана.
  const hiddenLabels = new Map<string, unknown>();
  const restoreLabels = () => {
    const map = ctx.getMap();
    for (const [layer, original] of hiddenLabels) {
      if (map?.getLayer(layer)) map.setPaintProperty(layer, "text-opacity", (original ?? 1) as never);
    }
    hiddenLabels.clear();
  };
  const hideLabelOf = (pointId: string) => {
    const map = ctx.getMap();
    const m = /^(osm|hiking):(\d+)$/.exec(pointId);
    if (!map || !m) return;
    for (const layer of POI_LABEL_LAYERS) {
      if (!map.getLayer(layer)) continue;
      const original = map.getPaintProperty(layer, "text-opacity");
      hiddenLabels.set(layer, original);
      map.setPaintProperty(
        layer,
        "text-opacity",
        ["case", ["all", ["==", ["get", "origin"], m[1]], ["==", ["to-string", ["get", "source_id"]], m[2]]], 0, original ?? 1] as never
      );
    }
  };
  const clearRoute = () => {
    const map = ctx.getMap();
    if (!map) return;
    if (map.getLayer(ROUTE_LAYER)) map.removeLayer(ROUTE_LAYER);
    if (map.getSource(ROUTE_SOURCE)) map.removeSource(ROUTE_SOURCE);
  };
  // Выбранная точка - DOM-пин-капля (остриё на точке) с глифом вида внутри, а без
  // глифа - просто пин. Он поверх всех слоёв карты и пинов узлов, и не гаснет по
  // LOD-зуму значков POI.
  // label - подпись под остриём (имя + высота): подпись самого слоя карты лежит
  // под DOM-меткой и пин её перекрывал, эта плашка - всегда выше пина.
  const addPin = (lat: number, lon: number, title: string, glyphKind: string, mapIconSvg: string | null, label: string | null = null) => {
    const map = ctx.getMap();
    if (!map) return;
    const el = markerElement(
      "search-pin",
      `<svg viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path class="search-pin__body" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z" />
        <circle class="search-pin__hole" cx="192" cy="192" r="144" />
      </svg>`,
      PIN_SIZE
    );
    // Глиф: значок самой карты (тот же, что у точки на ней), иначе глиф вида из карточки.
    if (mapIconSvg || hasKindGlyph(glyphKind)) {
      const slot = document.createElement("span");
      slot.className = "search-pin__glyph";
      if (mapIconSvg) slot.innerHTML = mapIconSvg;
      else render(h(kindIcon(glyphKind), { size: 18, "stroke-width": 2.2, "aria-hidden": "true" }), slot);
      el.appendChild(slot);
    }
    if (label) {
      const span = document.createElement("span");
      span.className = "search-pin__label";
      span.textContent = label;
      el.appendChild(span);
    }
    el.title = title;
    pins.push(new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([lon, lat]).addTo(map));
  };

  // Что реально закрывает карту: нижняя шторка (мобильный) и левая боковая
  // панель (десктоп). Без отступов пин/маршрут оказывался бы под ними.
  const coverPadding = (extra = 0) => {
    const side = document.querySelector<HTMLElement>("aside");
    const left = side && window.innerWidth >= 768 ? Math.round(side.getBoundingClientRect().right) : 0;
    return { top: extra, right: extra, bottom: extra + Math.round(Math.max(mapSheetInset.value, mapSheetCover.value)), left: extra + left };
  };

  // Для flyTo - одноразовый сдвиг цели в центр видимой части. padding тут
  // нельзя: он остаётся на карте и сдвигает getCenter(), от которого считается поиск.
  const coverOffset = (): [number, number] => {
    const p = coverPadding();
    return [p.left / 2, -p.bottom / 2];
  };

  // Точка на экране и не под шторкой/боковой панелью.
  const inUncoveredView = (lat: number, lon: number) => {
    const map = ctx.getMap();
    if (!map) return false;
    const { x, y } = map.project([lon, lat]);
    const c = map.getCanvas();
    const p = coverPadding();
    return x >= p.left && x <= c.clientWidth - p.right && y >= p.top && y <= c.clientHeight - p.bottom;
  };

  const draw = (f: SearchFocus | null, prev: SearchFocus | null) => {
    const map = ctx.getMap();
    if (!map || !ctx.isMapReady()) return;
    clearPins();
    clearRoute();
    restoreLabels();
    if (!f) return;
    if (f.kind === "route") {
      const coords = f.hit.line.map(([lat, lon]) => [lon, lat] as [number, number]);
      if (coords.length < 2) return;
      map.addSource(ROUTE_SOURCE, { type: "geojson", data: { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } } });
      map.addLayer({ id: ROUTE_LAYER, type: "line", source: ROUTE_SOURCE, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#2f6f55", "line-width": 5 } });
      const bounds = coords.reduce((acc, c) => acc.extend(c), new maplibregl.LngLatBounds(coords[0], coords[0]));
      map.fitBounds(bounds, { padding: coverPadding(48), duration: 600 });
      return;
    }
    const shownName = pickLocalizedName(f.hit.names, f.hit.name, ctx.locale.value);
    const poiLabel = f.kind === "poi" ? [shownName, f.hit.ele != null ? `${Math.round(f.hit.ele)} ${ctx.t("units.m")}` : ""].filter(Boolean).join(" ") : null;
    // Спасхижина уже стоит на карте собственным пином (usePoiLayers) - выбор не
    // перерисовывает его другим цветом и глифом.
    const ownPin = f.kind === "poi" && f.hit.osmKind === "rescue_hut";
    if (!ownPin) {
      addPin(f.hit.lat, f.hit.lon, shownName || f.hit.name, f.hit.osmKind, f.kind === "poi" ? buildPoiIconSvg(f.hit.osmKind) : null, poiLabel);
      if (f.kind === "poi") hideLabelOf(f.hit.id);
    }
    // Летим только при смене выбранного объекта, не при дорисовке соседних точек.
    if (prev && prev.kind !== "route" && prev.hit.id === f.hit.id) return;
    // Точку уже видно на близком зуме (обычно - тап по ней на карте): камеру не
    // дёргаем, только метка. Иначе - к ней, не отдаляя карту.
    if (map.getZoom() >= CLOSE_ZOOM && inUncoveredView(f.hit.lat, f.hit.lon)) return;
    map.flyTo({ center: [f.hit.lon, f.hit.lat], zoom: Math.max(map.getZoom(), FOCUS_ZOOM), duration: 700, offset: coverOffset() });
  };

  watch(() => store.focus, (f, prev) => draw(f, prev ?? null));
}
